'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppConfirmDialog } from '@/components/ui/alert-dialog';
import { logger } from '@/lib/logger';

/**
 * Imperative, promise-based confirm dialog (FE-002).
 *
 * The drop-in replacement for `window.confirm()` — keeps the call-site terse
 * while rendering the styled, accessible app dialog instead of native browser
 * chrome (which the project's no-browser-alerts rule forbids):
 *
 *   if (await confirmDialog({ message: 'Delete this?', destructive: true })) {
 *     await doDelete();
 *   }
 *
 * Mount <ConfirmDialogHost /> exactly once near the app root (layout.tsx).
 * Falls back to window.confirm only if the host isn't mounted yet.
 */

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
  type?: 'info' | 'success' | 'warning' | 'error';
}

type Resolver = (ok: boolean) => void;
let _open: ((opts: ConfirmOptions) => Promise<boolean>) | null = null;

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  if (_open) return _open(opts);
  // No host yet (SSR, or called before mount). Never fall back to
  // window.confirm — the browser's own chrome is exactly what this replaces.
  // Resolving false is the safe default: the guarded action does not run.
  if (typeof console !== 'undefined') logger.warn('[confirmDialog] host not mounted:', opts.title, opts.message);
  return Promise.resolve(false);
}

export function ConfirmDialogHost() {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<Resolver | null>(null);

  useEffect(() => {
    _open = (o: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
        setOpts(o);
      });
    return () => { _open = null; };
  }, []);

  // Resolve once, outside the state updater (idempotent via the ref).
  const settle = useCallback((ok: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOpts(null);
    resolve?.(ok);
  }, []);

  if (!opts) return null;

  return (
    <AppConfirmDialog
      open
      onOpenChange={(open) => { if (!open) settle(false); }}
      title={opts.title ?? 'Are you sure?'}
      description={opts.message}
      type={opts.type ?? (opts.destructive ? 'warning' : 'info')}
      confirmText={opts.confirmText ?? (opts.destructive ? 'Delete' : 'Confirm')}
      cancelText={opts.cancelText ?? 'Cancel'}
      destructive={opts.destructive}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );
}

export default ConfirmDialogHost;
